import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiLoader,
  FiRefreshCcw,
  FiDownload,
} from "react-icons/fi";
import axiosInstance from "../../../Utils/axiosInstance";
import * as echarts from "echarts";

const YEAR = [
  { label: "2026", value: 2026 },
  { label: "2025", value: 2025 },
];
const DEFAULT_YEAR = 2026;

const formatDashboardTitle = (name) =>
  name
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()) || "Dashboard Detail";

const MONTH_ORDER = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const parseMonthYear = (monthYear) => {
  if (!monthYear) return -Infinity;
  const [mon, year] = monthYear.split("_");
  const monthIndex = MONTH_ORDER[mon] ?? 0;
  return Number(year || 0) * 12 + monthIndex;
};

const sortByMonthYear = (arr, key = "Month_Year") => {
  if (!Array.isArray(arr)) return [];
  return [...arr].sort(
    (a, b) => parseMonthYear(a[key]) - parseMonthYear(b[key]),
  );
};

const ChartCard = ({ title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    {title && (
      <h3 className="text-lg font-bold text-slate-800 mb-3">{title}</h3>
    )}
    {children}
  </div>
);

const formatTableCellValue = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return value || "-";
  if (parsedValue === 0) return "0.00";
  return parsedValue.toFixed(2);
};

const isTotalRow = (particulars = "") => {
  const lower = particulars.toLowerCase();
  return (
    lower.startsWith("a. total") ||
    lower.startsWith("b. total") ||
    lower.startsWith("net cashflow") ||
    lower.startsWith("closing balance")
  );
};

const isNetRow = (particulars = "") =>
  particulars.toLowerCase().startsWith("net cashflow") ||
  particulars.toLowerCase().startsWith("closing balance");

// ─── Overview Tab Components ────────────────────────────────────────────────

const OverviewCard = ({ title, value, icon, colorClass }) => (
  <div className={`rounded-2xl p-5 shadow-sm ${colorClass}`}>
    <div className="flex items-start justify-between">
      <div className="flex flex-col">
        <p className="text-sm font-bold uppercase tracking-wide opacity-80">
          {title}
        </p>
        <p className="text-3xl font-bold mt-1">
          {formatTableCellValue(value)}
          <span className="text-lg font-medium opacity-80 ml-1">Cr</span>
        </p>
      </div>
      <div className="text-3xl opacity-70">{icon}</div>
    </div>
  </div>
);

const OverviewCards = ({ data }) => {
  if (!data) return null;

  const cards = [
    { data: data.card1, icon: "🏦", color: "bg-blue-800 text-blue-100" },
    { data: data.card2, icon: "📈", color: "bg-emerald-800 text-emerald-100" },
    { data: data.card3, icon: "📉", color: "bg-red-800 text-red-100" },
    { data: data.card4, icon: "💰", color: "bg-yellow-800 text-yellow-100" },
    { data: data.card5, icon: "📄", color: "bg-indigo-800 text-indigo-100" },
    { data: data.card6, icon: "⚖️", color: "bg-slate-800 text-slate-100" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map(({ data: cardData, icon, color }, index) => {
        if (!cardData || !cardData[0]) return null;
        const item = cardData[0];
        const title = Object.keys(item).find((k) => k !== "Value");
        const value = item.Value;
        return (
          <OverviewCard
            key={index}
            title={title}
            value={value}
            icon={icon}
            colorClass={color}
          />
        );
      })}
    </div>
  );
};

const getTrendChartOption = (chartData) => {
  const sortedData = sortByMonthYear(chartData || []);
  const labels = sortedData.map((d) => d.Month_Year.replace("_", " "));

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      confine: true,
      valueFormatter: (value) => `${formatTableCellValue(value)} Cr`,
    },
    legend: {
      data: ["Collections", "Payments", "Net Flow"],
      top: 0,
      icon: "circle",
      itemGap: 15,
    },
    grid: { left: 60, right: 20, bottom: 30, top: 50 },
    xAxis: {
      type: "category",
      boundaryGap: true,
      data: labels,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisLabel: { color: "#334155" },
    },
    yAxis: {
      type: "value",
      name: "Rs Cr",
      axisLabel: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#000",
      },
      splitLine: { lineStyle: { color: "#eef2ff" } },
    },
    series: [
      {
        name: "Collections",
        type: "bar",
        data: sortedData.map((d) => d.Collections),
        itemStyle: { color: "#036b47" },
      },
      {
        name: "Payments",
        type: "bar",
        data: sortedData.map((d) => d.Payments),
        itemStyle: { color: "#ba2b2b" },
      },
      {
        name: "Net Flow",
        type: "line",
        smooth: true,
        data: sortedData.map((d) => d.NetFlow),
        itemStyle: { color: "#f59e0b" },
        lineStyle: { width: 3 },
      },
    ],
  };
};

const OverviewTrendChart = ({ data }) => {
  const option = useMemo(() => getTrendChartOption(data), [data]);
  return (
    <ReactECharts option={option} style={{ height: 300 }} notMerge lazyUpdate />
  );
};

const getInflowDonutOption = (chartData) => {
  const total = chartData.reduce((sum, item) => sum + item.originalValue, 0);
  const colors = [
    "#854F0B",
    "#EF9F27",
    "#FAC775",
    "#FAEEDA",
    "#B8860B",
    "#C47A05",
  ];

  return {
    animation: false,
    tooltip: {
      trigger: "item",
      confine: true,
      formatter: ({ name, data: itemData, percent }) =>
        `${name}<br/><b>${formatTableCellValue(itemData.originalValue)} Cr</b> (${percent}%)`,
    },
    legend: { show: false },
    series: [
      {
        name: "Cash Inflow",
        type: "pie",
        radius: ["60%", "80%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
        data: chartData.map((d, i) => ({
          ...d,
          itemStyle: { color: colors[i % colors.length] },
        })),
      },
    ],
    graphic: [
      {
        type: "text",
        left: "center",
        top: "42%",
        style: {
          text: formatTableCellValue(total),
          fontSize: 24,
          fontWeight: 700,
          fill: "#1e293b",
        },
      },
      {
        type: "text",
        left: "center",
        top: "56%",
        style: { text: "Total Inflow", fontSize: 12, fill: "#64748b" },
      },
    ],
  };
};

const OverviewInflowChart = ({ data }) => {
  const chartData = useMemo(
    () =>
      (data || [])
        .map((d) => ({
          name: d.Description,
          value: Math.abs(d.Actuals),
          originalValue: d.Actuals,
        }))
        .sort((a, b) => b.value - a.value),
    [data],
  );

  const option = useMemo(() => getInflowDonutOption(chartData), [chartData]);
  const colors = [
    "#854F0B",
    "#EF9F27",
    "#FAC775",
    "#FAEEDA",
    "#B8860B",
    "#C47A05",
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
      <ReactECharts
        option={option}
        style={{ height: 260 }}
        notMerge
        lazyUpdate
      />
      <div className="space-y-2">
        {chartData.slice(0, 5).map((item, i) => (
          <div
            key={item.name}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: colors[i % colors.length] }}
              />
              <span className="text-slate-600 font-medium truncate">
                {item.name}
              </span>
            </div>
            <span className="font-mono font-semibold text-slate-700">
              {formatTableCellValue(item.originalValue)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const getOutflowBarChartOption = (chartData) => {
  const sortedData = [...(chartData || [])].sort(
    (a, b) => a.Actuals - b.Actuals,
  );
  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: "{b}: {c} Cr",
    },
    grid: { left: 20, right: 40, bottom: 30, top: 10, containLabel: true },
    xAxis: {
      type: "value",
      name: "Rs Cr",
      nameLocation: "middle",
      nameGap: 25,
      axisLabel: { color: "#334155" },
      splitLine: { lineStyle: { color: "#eef2ff" } },
    },
    yAxis: {
      type: "category",
      data: sortedData.map((d) => d.Description),
      axisLabel: {
        color: "#000",
        fontWeight: "bold",
        width: 140,
        overflow: "truncate",
      },
    },
    series: [
      {
        name: "Actuals (Cr)",
        type: "bar",
        data: sortedData.map((d) => d.Actuals),
        itemStyle: {
          color: new echarts.graphic.LinearGradient(1, 0, 0, 0, [
            { offset: 0, color: "#f87171" },
            { offset: 1, color: "#ba2b2b" },
          ]),
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  };
};

const OverviewOutflowChart = ({ data }) => {
  const option = useMemo(() => getOutflowBarChartOption(data), [data]);
  return (
    <ReactECharts option={option} style={{ height: 300 }} notMerge lazyUpdate />
  );
};

const getWeeklyBarChartOption = (chartData) => {
  if (!chartData || chartData.length === 0) return {};

  const collectionsRow = chartData.find(
    (d) => d.ParticularType === "CashInFlow",
  );
  const paymentsRow = chartData.find((d) => d.ParticularType === "CashOutFlow");

  if (!collectionsRow && !paymentsRow) return {};

  const weekKeys = Object.keys(collectionsRow || paymentsRow).filter(
    (k) => k.startsWith("WEEK") && k.endsWith("_Actuals"),
  );
  const labels = weekKeys.map((k) =>
    k.replace("_Actuals", "").replace("_", " "),
  );

  const collectionsData = weekKeys.map((k) => collectionsRow?.[k] ?? 0);
  const paymentsData = weekKeys.map((k) => paymentsRow?.[k] ?? 0);

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatTableCellValue(value)} Cr`,
    },
    legend: {
      data: ["Collections", "Payments"],
      top: 0,
      icon: "circle",
      itemGap: 15,
    },
    grid: { left: 60, right: 20, bottom: 30, top: 50 },
    xAxis: {
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisLabel: { color: "#334155" },
    },
    yAxis: {
      type: "value",
      name: "Rs Cr",
      axisLabel: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#000",
      },
      splitLine: { lineStyle: { color: "#eef2ff" } },
    },
    series: [
      {
        name: "Collections",
        type: "bar",
        data: collectionsData,
        itemStyle: { color: "#036b47" },
      },
      {
        name: "Payments",
        type: "bar",
        data: paymentsData,
        itemStyle: { color: "#ba2b2b" },
      },
    ],
  };
};

const OverviewWeeklyChart = ({ data }) => {
  const option = useMemo(() => getWeeklyBarChartOption(data), [data]);
  return (
    <ReactECharts option={option} style={{ height: 300 }} notMerge lazyUpdate />
  );
};

const SummaryReportTable = ({ title, data }) => {
  const { openingBalanceRow, cashInFlowRows, cashOutFlowRows } = useMemo(() => {
    if (!data || Array.isArray(data)) {
      return {
        openingBalanceRow: null,
        cashInFlowRows: Array.isArray(data) ? data : [],
        cashOutFlowRows: [],
      };
    }

    const allInFlow = data.CashInFlow || [];

    const openingBalanceRow = allInFlow.find(
      (row) =>
        row.Particulars?.toLowerCase().trim() === "opening balance - free cash",
    );

    const otherRows = allInFlow.filter(
      (row) =>
        row.Particulars?.toLowerCase().trim() !== "opening balance - free cash",
    );

    const inflowOrder = {
      "onshore ongoing projects": 1,
      "new customer advance": 2,
      service: 3,
      "confirming line": 4,
      "a. total collections": 5,
    };

    const sorted = [...otherRows].sort((a, b) => {
      const ao = inflowOrder[a.Particulars.toLowerCase().trim()] ?? 999;
      const bo = inflowOrder[b.Particulars.toLowerCase().trim()] ?? 999;
      return ao - bo;
    });

    const totalIndex = sorted.findIndex(
      (r) => r.Particulars.toLowerCase().trim() === "a. total collections",
    );

    const cashInFlowRows =
      totalIndex >= 0 ? sorted.slice(0, totalIndex + 1) : sorted;

    const remainingRows = totalIndex >= 0 ? sorted.slice(totalIndex + 1) : [];

    return {
      openingBalanceRow,
      cashInFlowRows,
      cashOutFlowRows: [...remainingRows, ...(data.CashOutFlow || [])],
    };
  }, [data]);

  const allRows = useMemo(() => {
    if (Array.isArray(data)) return data;

    return [
      openingBalanceRow,
      ...cashInFlowRows,
      ...(data?.CashOutFlow || []),
    ].filter(Boolean);
  }, [data, openingBalanceRow, cashInFlowRows]);

  const columns = useMemo(() => {
    if (!allRows.length) return [];

    const excludedKeys = new Set([
      "Section",
      "Particulars",
      "ParticularType",
      "Month_Year",
    ]);

    return Object.keys(allRows[0]).filter((key) => !excludedKeys.has(key));
  }, [allRows]);

  if (!allRows.length) return null;

  const renderRow = (row, index) => {
    const isTotal = isTotalRow(row.Particulars);
    const isNet = isNetRow(row.Particulars);

    if (isNet) {
      return (
        <tr
          key={`${row.Particulars}-${index}`}
          className="border-t-2 border-[#633806]"
        >
          <td className="px-4 py-2.5 font-bold text-[#FAEEDA] bg-[#854F0B]">
            {row.Particulars}
          </td>

          {columns.map((col) => (
            <td
              key={col}
              className="px-4 py-2.5 text-right font-mono font-bold text-[#FAEEDA] bg-[#854F0B]"
            >
              {formatTableCellValue(row[col])}
            </td>
          ))}
        </tr>
      );
    }

    if (isTotal) {
      return (
        <tr
          key={`${row.Particulars}-${index}`}
          className="border-t-2 border-[#EF9F27]"
        >
          <td className="px-4 py-2.5 font-bold text-[#412402] bg-[#FAC775]">
            {row.Particulars}
          </td>

          {columns.map((col) => (
            <td
              key={col}
              className="px-4 py-2.5 text-right font-mono font-bold text-[#412402] bg-[#FAC775]"
            >
              {formatTableCellValue(row[col])}
            </td>
          ))}
        </tr>
      );
    }

    return (
      <tr
        key={`${row.Particulars}-${index}`}
        className="border-t border-[#FAF0DC] hover:bg-[#FEF8EE]"
      >
        <td className="px-4 py-2.5 font-medium text-[#5a3a1a]">
          {row.Particulars}
        </td>

        {columns.map((col) => {
          const value = Number(row[col]);

          return (
            <td
              key={col}
              className={`px-4 py-2.5 text-right font-mono ${
                value < 0 ? "text-red-700" : value > 0 ? "text-emerald-700" : ""
              }`}
            >
              {formatTableCellValue(row[col])}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderSubheading = (text) => (
    <tr key={text} className="bg-[#FAEEDA]">
      <th
        colSpan={columns.length + 1}
        className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[#854F0B]"
      >
        {text}
      </th>
    </tr>
  );

  return (
    <div className="rounded-2xl border border-[#EFD99A] bg-white shadow-sm">
      <div className="border-b border-[#EFD99A] px-4 py-3">
        <h2 className="text-lg font-semibold text-[#412402]">{title}</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#854F0B] text-left text-xs font-semibold uppercase tracking-wider text-[#FAEEDA]">
            <tr>
              <th className="w-1/3 px-4 py-3">Particulars</th>

              {columns.map((col) => (
                <th key={col} className="px-4 py-3 text-right">
                  {col.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Array.isArray(data) ? (
              data.map((row, index) => renderRow(row, index))
            ) : (
              <>
                {/* Opening Balance */}
                {openingBalanceRow && renderRow(openingBalanceRow, "opening")}

                {/* Cash Inflow */}
                {cashInFlowRows.length > 0 && (
                  <>
                    {renderSubheading("Cash Inflow")}
                    {cashInFlowRows.map((row, index) =>
                      renderRow(row, `in-${index}`),
                    )}
                  </>
                )}

                {/* Cash Outflow (API order unchanged) */}
                {cashOutFlowRows.length > 0 && (
                  <>
                    {renderSubheading("Cash Outflow")}
                    {cashOutFlowRows.map((row, i) =>
                      renderRow(row, `out-${i}`),
                    )}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MisReportTable = ({ title, data }) => {
  const MONTH_ORDER = useMemo(
    () => ({
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    }),
    [],
  );

  const isMisNetRow = (description = "") => {
    const lower = description.toLowerCase();
    return lower.startsWith("closing balance") || lower.startsWith("net cash");
  };

  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];
    const firstRow = data[0];
    const allColumns = Object.keys(firstRow).filter(
      (key) => key !== "Description",
    );

    return allColumns.sort((a, b) => {
      const monthA = a.split("_")[0];
      const monthB = b.split("_")[0];
      return MONTH_ORDER[monthA] - MONTH_ORDER[monthB];
    });
  }, [data, MONTH_ORDER]);

  const isMisTotalRow = (description = "") => {
    const lower = description.toLowerCase();
    return lower.startsWith("total");
  };

  if (!data || data.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[#EFD99A] bg-white shadow-sm">
      <div className="border-b border-[#EFD99A] px-4 py-3">
        <h2 className="text-lg font-semibold text-[#412402]">
          {title} — ₹ Crores
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-slate-700">
          <thead className="bg-[#854F0B] text-left text-xs font-semibold uppercase tracking-wider text-[#FAEEDA]">
            <tr>
              <th className="w-1/3 px-4 py-3">Description</th>
              {columns.map((col) => (
                <th key={col} className="px-4 py-3 text-right">
                  {col.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const isNet = isMisNetRow(row.Description);
              const isTotal = isMisTotalRow(row.Description);

              if (isNet) {
                return (
                  <tr
                    key={`${row.Description}-${index}`}
                    className="border-t-2 border-[#633806]"
                  >
                    <td className="px-4 py-2.5 font-bold text-[#FAEEDA] bg-[#854F0B]">
                      {row.Description}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col}
                        className="px-4 py-2.5 text-right font-mono font-bold text-[#FAEEDA] bg-[#854F0B]"
                      >
                        {formatTableCellValue(row[col])}
                      </td>
                    ))}
                  </tr>
                );
              }

              if (isTotal) {
                return (
                  <tr
                    key={`${row.Description}-${index}`}
                    className="border-t-2 border-[#EF9F27]"
                  >
                    <td className="px-4 py-2.5 font-bold text-[#412402] bg-[#FAC775]">
                      {row.Description}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col}
                        className="px-4 py-2.5 text-right font-mono font-bold text-[#412402] bg-[#FAC775]"
                      >
                        {formatTableCellValue(row[col])}
                      </td>
                    ))}
                  </tr>
                );
              }

              return (
                <tr
                  key={`${row.Description}-${index}`}
                  className="border-t border-[#FAF0DC] hover:bg-[#FEF8EE]"
                >
                  <td className="px-4 py-2.5 font-medium text-[#5a3a1a]">
                    {row.Description}
                  </td>
                  {columns.map((col) => {
                    const value = Number(row[col]);
                    return (
                      <td
                        key={col}
                        className={`px-4 py-2.5 text-right font-mono ${
                          !isTotal && !isNet && value < 0 ? "text-red-700" : ""
                        } ${!isTotal && !isNet && value > 0 ? "text-emerald-700" : ""}`}
                      >
                        {formatTableCellValue(row[col])}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EDACashFlowDashboardDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dashboard, selectedProcess } = location.state || {};

  const [monthPeriods, setMonthPeriods] = useState([]);
  const [weekPeriods, setWeekPeriods] = useState([]);
  const [selectedMonthPeriod, setSelectedMonthPeriod] = useState(null);
  const [selectedWeekPeriod, setSelectedWeekPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);

  // MIS Report tab state
  const [misSelectedMonthPeriod, setMisSelectedMonthPeriod] = useState(null);
  const [misSelectedYear, setMisSelectedYear] = useState(DEFAULT_YEAR);
  const [misReportData, setMisReportData] = useState(null); // Placeholder for data
  const [misLoading, setMisLoading] = useState(false);
  const [misError, setMisError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Overview tab state
  const [overviewMonths, setOverviewMonths] = useState([]);
  const [overviewSelectedMonth, setOverviewSelectedMonth] = useState(null);
  const [overviewSelectedYear, setOverviewSelectedYear] =
    useState(DEFAULT_YEAR);
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const title = formatDashboardTitle(dashboard?.eda_name || "Cash Flow");

  useEffect(() => {
    let ignore = false;
    const fetchPeriods = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          "/eda4/cashflow-period-dropdown",
        );
        if (!ignore && response.data?.status) {
          const items = response.data.items;
          const months = items.months || [];
          const weeks = items.weeks || [];
          setMonthPeriods(months);
          setWeekPeriods(weeks);

          if (months.length > 0) {
            setSelectedMonthPeriod(months[0]);
            setMisSelectedMonthPeriod(months[0]);
          }
          if (weeks.length > 0) {
            setSelectedWeekPeriod(weeks[0]);
          }
        }
      } catch (err) {
        if (!ignore) {
          setError("Failed to fetch period options.");
          console.error("Failed to fetch period options", err);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchPeriods();
    return () => {
      ignore = true;
    };
  }, []);

  // Fetch main dashboard data
  useEffect(() => {
    if (
      activeTab !== "summary" ||
      !location.state ||
      !selectedMonthPeriod ||
      !selectedWeekPeriod ||
      !selectedYear
    ) {
      return;
    }

    let ignore = false;
    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.post("/eda4/summary-dashboard", {
          Month: selectedMonthPeriod.value,
          Year: String(selectedYear),
          Week: selectedWeekPeriod.value,
        });
        if (!ignore && response.data?.status) {
          setDashboardData(response.data.items);
        } else if (!ignore) {
          setError(response.data?.message || "Failed to fetch dashboard data.");
          setDashboardData(null);
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.response?.data?.message ||
              "Failed to fetch dashboard data.",
          );
          setDashboardData(null);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      ignore = true;
    };
  }, [
    activeTab,
    location.state,
    selectedYear,
    selectedMonthPeriod,
    selectedWeekPeriod,
  ]);

  // Fetching MIS report data
  useEffect(() => {
    if (activeTab !== "mis" || !misSelectedMonthPeriod || !misSelectedYear) {
      return;
    }

    let ignore = false;
    const fetchMisReportData = async () => {
      setMisLoading(true);
      setMisError("");
      setMisReportData(null);
      try {
        const response = await axiosInstance.post("/eda4/mis-report", {
          Month: misSelectedMonthPeriod.value,
          Year: String(misSelectedYear),
        });
        if (!ignore && response.data?.status) {
          setMisReportData(response.data.items);
        } else if (!ignore) {
          setMisError(response.data?.message || "Failed to fetch MIS report.");
        }
      } catch (apiError) {
        if (!ignore) {
          setMisError(
            apiError?.response?.data?.message || "Failed to fetch MIS report.",
          );
        }
      } finally {
        if (!ignore) {
          setMisLoading(false);
        }
      }
    };

    fetchMisReportData();
    return () => {
      ignore = true;
    };
  }, [activeTab, misSelectedMonthPeriod, misSelectedYear]);

  // Fetching Overview month dropdown
  useEffect(() => {
    if (activeTab !== "overview") {
      return;
    }

    let ignore = false;
    const fetchOverviewMonths = async () => {
      setOverviewLoading(true);
      setOverviewError("");
      try {
        const response = await axiosInstance.get(
          "/eda4/cashflow-month-dropdown",
        );
        if (!ignore && response.data?.status) {
          const months = response.data.items || [];
          setOverviewMonths(months);
          if (months.length > 0) {
            const currentMonth = months.find((m) =>
              m.label.includes("Current Month"),
            );
            setOverviewSelectedMonth(currentMonth || months[0]);
          }
        } else if (!ignore) {
          setOverviewError(
            response.data?.message || "Failed to fetch overview months.",
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setOverviewError(
            apiError?.response?.data?.message ||
              "Failed to fetch overview months.",
          );
        }
      } finally {
        if (!ignore) {
          setOverviewLoading(false);
        }
      }
    };

    fetchOverviewMonths();
    return () => {
      ignore = true;
    };
  }, [activeTab]);

  // Fetching Overview data
  useEffect(() => {
    if (
      activeTab !== "overview" ||
      !overviewSelectedMonth ||
      !overviewSelectedYear
    ) {
      return;
    }

    let ignore = false;
    const fetchOverviewData = async () => {
      setOverviewLoading(true);
      setOverviewError("");
      setOverviewData(null);
      try {
        const response = await axiosInstance.post("/eda4/overview-dashboard", {
          Month: overviewSelectedMonth.value,
          Year: String(overviewSelectedYear),
        });

        if (!ignore && response.data?.status) {
          setOverviewData(response.data.items);
        } else if (!ignore) {
          setOverviewError(
            response.data?.message || "Failed to fetch overview data.",
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setOverviewError(
            apiError?.response?.data?.message ||
              "Failed to fetch overview data.",
          );
        }
      } finally {
        if (!ignore) {
          setOverviewLoading(false);
        }
      }
    };

    fetchOverviewData();

    return () => {
      ignore = true;
    };
  }, [activeTab, overviewSelectedMonth, overviewSelectedYear]);

  const handleReset = () => {
    if (monthPeriods.length > 0) {
      setSelectedMonthPeriod(monthPeriods[0]);
    }
    if (weekPeriods.length > 0) {
      setSelectedWeekPeriod(weekPeriods[0]);
    }
    setSelectedYear(DEFAULT_YEAR);
  };

  const handleOverviewReset = () => {
    if (overviewMonths.length > 0) {
      const currentMonth = overviewMonths.find((m) =>
        m.label.includes("Current Month"),
      );
      setOverviewSelectedMonth(currentMonth || overviewMonths[0]);
    }
    setOverviewSelectedYear(DEFAULT_YEAR);
  };

  const handleMisReset = () => {
    if (monthPeriods.length > 0) {
      setMisSelectedMonthPeriod(monthPeriods[0]);
    }
    setMisSelectedYear(DEFAULT_YEAR);
  };

  const TabButton = ({ label, tabName }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tabName)}
      className={`px-4 py-3 text-sm font-semibold transition-colors duration-200 focus:outline-none ${
        activeTab === tabName
          ? "border-b-2 border-blue-600 text-blue-600"
          : "text-slate-500 hover:text-slate-800"
      }`}
    >
      {label}
    </button>
  );

  const hasSummaryData = useMemo(() => {
    if (!dashboardData) return false;

    return Object.values(dashboardData).some((section) => {
      // Investment Summary
      if (Array.isArray(section)) {
        return section.length > 0;
      }

      // Operations / Equity & Investment
      return (
        (section?.CashInFlow?.length ?? 0) > 0 ||
        (section?.CashOutFlow?.length ?? 0) > 0
      );
    });
  }, [dashboardData]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Process: {formatDashboardTitle(selectedProcess)} | Status:{" "}
              {dashboard?.edaStatus || "Available"}
            </p>
          </div>

          <div className="flex items-center gap-2 self-start">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200">
          <div className="flex items-center -mb-px">
            <TabButton label="Overview" tabName="overview" />
            <TabButton label="Summary" tabName="summary" />
            <TabButton label="MIS Report" tabName="mis" />
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="flex flex-col gap-3 pt-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={overviewSelectedYear}
                  onChange={(event) =>
                    setOverviewSelectedYear(Number(event.target.value))
                  }
                  disabled={overviewLoading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEAR.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">
                  Month
                </span>
                <select
                  value={overviewSelectedMonth?.value || ""}
                  onChange={(e) => {
                    const period = overviewMonths.find(
                      (p) => p.value == e.target.value,
                    );
                    setOverviewSelectedMonth(period);
                  }}
                  disabled={overviewLoading || overviewMonths.length === 0}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {overviewMonths.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleOverviewReset}
                disabled={overviewLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {overviewLoading ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                  <FiCalendar className="h-4 w-4" />
                  Current period: {overviewSelectedMonth?.label || "N/A"}{" "}
                  {overviewSelectedYear}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "summary" && (
          <>
            {!location.state ? (
              <div className="pt-4 rounded-xl border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Open this page from the dashboard list so the selected dashboard
                context is available.
              </div>
            ) : (
              <div className="flex flex-col gap-3 pt-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      Year
                    </span>
                    <select
                      value={selectedYear}
                      onChange={(event) =>
                        setSelectedYear(Number(event.target.value))
                      }
                      disabled={loading}
                      className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                    >
                      {YEAR.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      Monthly Period
                    </span>
                    <select
                      value={selectedMonthPeriod?.value || ""}
                      onChange={(e) => {
                        const period = monthPeriods.find(
                          (p) => p.value == e.target.value,
                        );
                        setSelectedMonthPeriod(period);
                      }}
                      disabled={loading || monthPeriods.length === 0}
                      className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                    >
                      {monthPeriods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      Weekly Period
                    </span>
                    <select
                      value={selectedWeekPeriod?.value || ""}
                      onChange={(e) => {
                        const period = weekPeriods.find(
                          (p) => p.value == e.target.value,
                        );
                        setSelectedWeekPeriod(period);
                      }}
                      disabled={loading || weekPeriods.length === 0}
                      className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                    >
                      {weekPeriods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
                  >
                    <FiRefreshCcw className="h-3.5 w-3.5" />
                    Reset
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-1">
                  {loading ? (
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                      <FiLoader className="h-4 w-4 animate-spin" />
                      Updating dashboard...
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                      <FiCalendar className="h-4 w-4" />
                      Current period: {selectedMonthPeriod?.label || "N/A"}{" "}
                      {selectedYear}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "mis" && (
          <div className="flex flex-col gap-3 pt-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={misSelectedYear}
                  onChange={(event) =>
                    setMisSelectedYear(Number(event.target.value))
                  }
                  disabled={misLoading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEAR.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">
                  Monthly Period
                </span>
                <select
                  value={misSelectedMonthPeriod?.value || ""}
                  onChange={(e) => {
                    const period = monthPeriods.find(
                      (p) => p.value == e.target.value,
                    );
                    setMisSelectedMonthPeriod(period);
                  }}
                  disabled={misLoading || monthPeriods.length === 0}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {monthPeriods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleMisReset}
                disabled={misLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {misLoading ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Updating report...
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                  <FiCalendar className="h-4 w-4" />
                  Current period: {misSelectedMonthPeriod?.label || "N/A"}{" "}
                  {misSelectedYear}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {activeTab === "summary" && (
        <div className="pt-4 space-y-4">
          {loading && !dashboardData ? (
            <div className="flex items-center justify-center p-10">
              <FiLoader className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading Summary...</span>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : hasSummaryData ? (
            <div className="space-y-6">
              {Object.entries(dashboardData).map(([title, data]) => (
                <SummaryReportTable key={title} title={title} data={data} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
              <h3 className="text-lg font-semibold text-slate-700">
                No Summary Data
              </h3>
              <p className="mt-2">
                No summary data available for the selected period.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "overview" && (
        <div className="pt-4 space-y-4">
          {overviewLoading && (
            <div className="flex items-center justify-center p-10">
              <FiLoader className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading Overview...</span>
            </div>
          )}
          {overviewError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {overviewError}
            </div>
          )}
          {!overviewLoading && !overviewError && overviewData && (
            <div className="space-y-6">
              <OverviewCards data={overviewData} />
              <ChartCard title="Monthly Trend - Collections vs Payments">
                <OverviewTrendChart data={overviewData.chart1} />
              </ChartCard>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="Cash Inflow Breakup">
                  <OverviewInflowChart data={overviewData.chart2} />
                </ChartCard>
                <ChartCard title="Cash Outflow Breakup">
                  <OverviewOutflowChart data={overviewData.chart4} />
                </ChartCard>
              </div>
              <ChartCard title="Weekly Trend - Collections vs Payments">
                <OverviewWeeklyChart data={overviewData.chart3} />
              </ChartCard>
              {overviewData.excelFiles &&
                overviewData.excelFiles[0]?.xlsLink && (
                  <div className="flex justify-end pt-2">
                    <a
                      href={overviewData.excelFiles[0].xlsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-[#0060D0] px-4 py-2 text-sm font-semibold !text-white shadow-sm transition hover:bg-[#0050b0]"
                    >
                      <FiDownload className="h-4 w-4" />
                      {overviewData.excelFiles[0].id ||
                        "Download Consolidated Report"}
                    </a>
                  </div>
                )}
            </div>
          )}
          {!overviewLoading && !overviewError && !overviewData && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
              <h3 className="text-lg font-semibold text-slate-700">
                No Overview Data
              </h3>
              <p className="mt-2">
                No overview data available for the selected period.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "mis" && (
        <div className="pt-4 space-y-4">
          {misLoading ? (
            <div className="flex items-center justify-center p-10">
              <FiLoader className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading MIS Report...</span>
            </div>
          ) : misError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {misError}
            </div>
          ) : misReportData && Object.keys(misReportData).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(misReportData).map(([title, data]) => (
                <MisReportTable key={title} title={title} data={data} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
              <h3 className="text-lg font-semibold text-slate-700">
                No MIS Report Data
              </h3>
              <p className="mt-2">
                No MIS report data available for the selected period.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EDACashFlowDashboardDetail;
